/**
 * DemoRequestDialog — the shared landing-page contact popup, hand-built (RetroUI has
 * no Dialog/Modal primitive, so we assemble the overlay + panel ourselves).
 *
 * ONE dialog serves BOTH landing CTAs — "Schedule a demo" (FinalCtaSection) and
 * "Talk to us about your study" (HeroSection) — via the ContactDialogProvider. The
 * `source` prop swaps the title/subtitle/button copy and is forwarded to the backend
 * so the notification email subject reflects which CTA opened it.
 *
 * The form itself (fields + validation + POST + success state) lives in the shared
 * DemoRequestForm, which ALSO backs the standalone /schedule-demo page — so the
 * popup and the page never drift. This file owns only the modal chrome: overlay,
 * panel, the X button, and Escape-to-close.
 *
 * Rendered inside the `.retroui-root` ancestor, so brand Tailwind tokens + the
 * scoped Preflight-off reset apply. Because Preflight is OFF, every border here is
 * written explicitly as `border-solid` — otherwise the border style is `none` and
 * the hard brutalist outline is invisible.
 *
 * Behaviour:
 *   - Renders null when `open` is false (so the form remounts fresh on every open).
 *   - Fixed overlay + dimmed backdrop; clicking the backdrop or the X closes.
 *   - Clicking the panel itself does NOT close (stopPropagation).
 *   - Escape closes; the first field is focused on open (DemoRequestForm autoFocus).
 */
import React from 'react';
import { X } from 'lucide-react';
import DemoRequestForm, { ContactSource } from './DemoRequestForm';

// Re-export so existing importers (ContactDialogProvider) keep their import path.
export type { ContactSource };

export interface DemoRequestDialogProps {
  open: boolean;
  onClose: () => void;
  /** Defaults to 'demo' (the FinalCtaSection "Schedule a demo" CTA). */
  source?: ContactSource;
}

const DemoRequestDialog: React.FC<DemoRequestDialogProps> = ({ open, onClose, source = 'demo' }) => {
  // Close on Escape while the dialog is open.
  React.useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    // Full-screen overlay. Clicking the backdrop (this element) closes the dialog.
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      {/* Modal panel. stopPropagation so inner clicks never bubble to the backdrop. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Contact StudySync"
        onClick={(e) => e.stopPropagation()}
        className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-none border-2 border-solid border-black bg-card p-8 shadow-lg md:p-12"
      >
        {/* Close button (top-right). */}
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute right-4 top-4 flex h-9 w-9 cursor-pointer items-center justify-center rounded-none border-2 border-solid border-black bg-white text-foreground transition hover:-translate-y-[1px] hover:bg-background focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          <X className="h-5 w-5" strokeWidth={2.5} aria-hidden="true" />
        </button>

        {/* The shared form — heading on (modal context), first field auto-focused, and
            the success-state action button closes the dialog. */}
        <DemoRequestForm source={source} showHeading autoFocus onDone={onClose} />
      </div>
    </div>
  );
};

export default DemoRequestDialog;
